import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { devicesActions, staffActions, uiActions } from '@/store';
import { getVehicleStatus, formatSpeed, isStationaryFix, STATUS_CONFIG } from '@/lib/utils';
import { isPositionStale, staleAgeLabel, lastPositionUpdate } from '@/lib/positionFreshness';
import useUnits from '@/hooks/useUnits';
import {
  Search, Grid3X3, List, Power, Locate, AlertTriangle,
  MapPin, Navigation, X, Check, Columns3, Terminal, Share2, Car, History,
  Edit3, Trash2, Eye, EyeOff, GripVertical, Star, Users, User, ChevronRight, Layers,
} from 'lucide-react';
import ColdTempChip, { ColdChipWrap } from '@/features/cold-chain/ColdTempChip';
import { useVirtualizer } from '@tanstack/react-virtual';
import { parseSensorConfig, resolveColorInterval, colorStatusToHex } from '@/lib/sensorUtils';
import { resolveFuelLevel } from '@/features/fuel/fuelLevelResolver';
import useFuelLevels from '@/features/fuel/useFuelLevels';
import { FlaskConical } from 'lucide-react';
import DeviceMarkerIcon from '@/components/shared/DeviceMarkerIcon';
import StatusIndicator from '@/components/shared/StatusIndicator';
import Tooltip from '@/components/shared/Tooltip';
import DockResizeHandle from '@/components/shared/DockResizeHandle';
import SendCommandDialog from '@/components/shared/SendCommandDialog';
import ShareDialog from '@/features/sharing/ShareDialog';
import DeviceDrawer from '@/components/shared/DeviceDrawer';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { showToast } from '@/components/shared/Toast';
import ColumnManager from '@/components/shared/ColumnManager';
import SearchableSelect from '@/components/shared/SearchableSelect';
import StaffGroupSelect from '@/components/shared/StaffGroupSelect';
import api from '@/lib/api';
import { getDriverForPosition } from '@/features/drivers/driverUtils';
import useAddressLabel from '@/hooks/useAddressLabel';
import useFavoriteVehicles from '@/hooks/useFavoriteVehicles';
import useStatusThresholds from '@/hooks/useStatusThresholds';
import { useColumnWidths } from '@/hooks/useColumnWidths';
import useVehicleStatuses from '@/hooks/useVehicleStatuses';
import VehicleContextMenu from '@/components/shared/VehicleContextMenu';
import StaffContextMenu from '@/components/shared/StaffContextMenu';
import { getStaffStatus } from '@/lib/getStaffStatus';
import { roleLabel } from '@/features/staff/attendanceModel';
import { groupDevicesIntoVehicles } from './vehicleGrouping';
import { companyOptions, deviceInCompany, isCompanyFilterActive } from './companyFilter';
import { useAccordion } from '@/hooks/useAccordion';
import dayjs from 'dayjs';

// Tiny hex→rgba helper for the per-status active-pill tint. Kept local —
// only this file needs it, and pulling in a color lib for one call site
// would be heavier than the 4 lines.
const hexToRgba = (hex, alpha) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// Active-pill styling: pale tint of the status color (preserves the
// icon's color identity instead of washing it out with a uniform green
// gradient). The 'All' pill falls through to a neutral accent provided
// by the caller (emerald for vehicles, blue for people).
const activePillStyle = (color) => ({
  background: hexToRgba(color, 0.16),
  border: `1px solid ${hexToRgba(color, 0.45)}`,
  color,
});

/** Status filter buttons config — vehicle row */
// Status filter pill keys. The actual icon + color come from StatusIndicator
// (which reads STATUS_CONFIG), so we don't carry a redundant color here —
// that mirror caused drift during the 2026-05-23 palette refresh and is gone.
const STATUS_FILTERS = [
  { key: 'all' },
  { key: 'moving' },
  { key: 'stopped' },
  { key: 'idle' },
  { key: 'pending' },
  { key: 'offline' },
];

// English fallbacks for the per-status tooltip descriptions. `t()` resolves
// `status.<key>Desc` first; if a locale hasn't translated them yet the user
// still sees a meaningful hover. Keep these short — native title tooltips
// don't wrap reliably across browsers.
const STATUS_DESC_FALLBACK = {
  moving: 'Vehicle is in motion. Last GPS fix is fresh and speed is above the moving threshold.',
  stopped: 'Vehicle is parked with ignition off.',
  idle: 'Vehicle is stopped with ignition still on (idling).',
  pending: 'Tracker is alive but the GPS fix is stale or the vehicle stopped reporting fresh fixes.',
  offline: 'Tracker connection lost — the server has not heard from this device in a while.',
};

/** Status filter buttons config — people row (Phase 3 polish) */
const PEOPLE_STATUS_FILTERS = [
  { key: 'all',       color: null,      labelKey: 'vehicleList.all' },
  { key: 'on_shift',  color: '#10b981', labelKey: 'people.status.onShift' },
  { key: 'off_shift', color: '#94a3b8', labelKey: 'people.status.offShift' },
  { key: 'overdue',   color: '#f59e0b', labelKey: 'people.status.overdue' },
  { key: 'sos',       color: '#dc2626', labelKey: 'people.status.sos' },
];

/** Column definitions (returns translated labels + dynamic sensor columns) */
function getAllColumns(t, devices) {
  const staticCols = [
    { id: 'status', label: t('columns.status'), width: 28, on: true, locked: true },
    { id: 'name', label: t('columns.name'), width: 'flex', on: true, locked: true },
    { id: 'speed', label: t('columns.speed'), width: 60, on: true },
    { id: 'lastUpdate', label: t('columns.lastUpdate'), width: 50, on: true },
    { id: 'address', label: t('columns.address'), width: 120 },
    { id: 'driver', label: t('columns.driver'), width: 80 },
    { id: 'ignition', label: t('columns.ignition'), width: 38 },
    { id: 'satellites', label: t('columns.satellites'), width: 38 },
    { id: 'battery', label: t('columns.battery'), width: 45 },
    { id: 'heading', label: t('columns.heading'), width: 42 },
    { id: 'fuel', label: t('columns.fuel', 'Fuel'), width: 65 },
    { id: 'uniqueId', label: t('columns.identifier'), width: 90 },
    { id: 'phone', label: t('columns.phone'), width: 80 },
    { id: 'model', label: t('columns.model'), width: 80 },
  ];

  // Discover unique sensor definitions across all devices
  const sensorMap = new Map();
  if (devices) {
    Object.values(devices).forEach((device) => {
      const config = parseSensorConfig(device?.attributes?.sensorConfig);
      config.forEach((s) => {
        if (!sensorMap.has(s.attribute)) sensorMap.set(s.attribute, s);
      });
    });
  }

  const dynamicCols = Array.from(sensorMap.values()).map((s) => ({
    id: `sensor_${s.attribute}`,
    label: s.name,
    width: 55,
    on: false,
    isSensor: true,
    sensorDef: s,
  }));

  return [...staticCols, ...dynamicCols];
}

/** Default column IDs */
const DEFAULT_VISIBLE = ['status', 'name', 'speed', 'lastUpdate'];

/** Column definitions for the People section in compact mode. Parallel to
 *  getAllColumns; people have a different shape so they can't share defs. */
function getAllPeopleColumns(t) {
  return [
    { id: 'status',   label: t('columns.status'), width: 28, locked: true },
    { id: 'name',     label: t('columns.name'),   width: 'flex', locked: true },
    { id: 'role',     label: t('people.col.role', 'Role'), width: 70 },
    { id: 'lastFix',  label: t('people.col.lastFix', 'Last fix'), width: 60 },
    { id: 'shift',    label: t('people.col.shiftStarted', 'Shift'), width: 70 },
    { id: 'phone',    label: t('columns.phone'), width: 100 },
    { id: 'battery',  label: t('columns.battery'), width: 45 },
    { id: 'speed',    label: t('columns.speed'),  width: 50 },
  ];
}

const DEFAULT_VISIBLE_PEOPLE = ['status', 'name', 'role', 'lastFix'];

/** Frosted glass style helper */
const frost = () => ({
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
});

/** Format relative time */
function relativeTime(timestamp) {
  if (!timestamp) return '—';
  const d = dayjs(timestamp);
  const now = dayjs();
  const diffMin = now.diff(d, 'minute');
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = now.diff(d, 'hour');
  if (diffHr < 24) return `${diffHr}h`;
  return d.format('MMM D');
}

/** Enrich a device with its position data for display. `status` is computed
 *  by the parent via useVehicleStatuses so every surface classifies from the
 *  SAME raw-position inputs (the sidebar-vs-map drift bug came back during
 *  animation legs when each surface called getVehicleStatus independently). */
function enrichDevice(device, position, thresholds, status, speedUnit = 'kmh') {
  const staleMs = (thresholds?.staleMinutes ?? 10) * 60_000;
  // Stationary-fix guard: parked-with-ignition-off + low GPS-Doppler speed
  // is jitter, not motion. Mirror the suppression the TelemetryPanel does
  // so the list and the detail panel show the same headline numbers.
  const stationary = isStationaryFix(position, thresholds);
  // "Updated" = last GPS fix (lastUpdate only when there is no position),
  // the same instant the status classifier reads — see lastPositionUpdate
  // for why max(fixTime, lastUpdate) is wrong for a parked tracker.
  const updatedAt = lastPositionUpdate(position, device);
  return {
    id: device.id,
    name: device.name,
    category: device.category,
    attributes: device.attributes,
    status,
    speed: stationary ? 0 : (position?.speed ? formatSpeed(position.speed, speedUnit) : 0),
    rawSpeed: stationary ? 0 : (position?.speed || 0),
    positionStale: isPositionStale(position, staleMs),
    positionAgeLabel: staleAgeLabel(position, staleMs),
    course: stationary ? 0 : (position?.course || 0),
    lastUpdate: updatedAt,
    ignition: position?.attributes?.ignition ?? false,
    satellites: position?.attributes?.sat ?? position?.attributes?.satellites ?? 0,
    battery: position?.attributes?.batteryLevel ?? null,
    address: position?.address || '',
    latitude: position?.latitude,
    longitude: position?.longitude,
    driver: device.attributes?.driver || '',
    uniqueId: device.uniqueId || '',
    phone: device.phone || '',
    model: device.model || '',
    // Enterprise view only: the device's (canonical) company.
    companyName: device.companyName || '',
  };
}

/** Enterprise view: the company a vehicle belongs to, under its name. */
function CompanyLine({ name, className = 'text-[9px]' }) {
  return (
    <span data-testid="vehicle-company" className={`block truncate font-medium text-slate-500 dark:text-slate-400 ${className}`}>
      <bdi>{name}</bdi>
    </span>
  );
}

/** Cell renderer for compact mode */
function CellValue({ colId, col, vehicle, t, position, drivers, devices, fuelByDevice }) {
  // Handle dynamic sensor columns
  if (col?.isSensor) {
    const rawValue = position?.attributes?.[col.sensorDef.attribute] ?? null;
    const colorStatus = resolveColorInterval(rawValue != null ? Number(rawValue) : null, col.sensorDef.intervals);
    return (
      <span style={colorStatus ? { color: colorStatusToHex(colorStatus) } : undefined}>
        {rawValue != null ? rawValue : '—'}
        {col.sensorDef.unit ? ` ${col.sensorDef.unit}` : ''}
      </span>
    );
  }

  const sc = STATUS_CONFIG[vehicle.status];
  const map = {
    status: () => (
      <span className="relative flex justify-center items-center">
        <StatusIndicator
          status={vehicle.status}
          size={14}
          title={`${t(`status.${vehicle.status}`)} — ${t(`status.${vehicle.status}Desc`, STATUS_DESC_FALLBACK[vehicle.status] || '')}`}
        />
        {vehicle.status === 'moving' && (
          <span className="absolute w-[7px] h-[7px] rounded-full animate-ping opacity-40" style={{ backgroundColor: sc.color }} />
        )}
      </span>
    ),
    name: () => (vehicle.companyName ? (
      <span className="flex flex-col min-w-0 leading-tight">
        <span className="truncate font-semibold text-slate-800 dark:text-slate-200"><bdi>{vehicle.name}</bdi></span>
        <CompanyLine name={vehicle.companyName} />
      </span>
    ) : <span className="truncate font-semibold text-slate-800 dark:text-slate-200"><bdi>{vehicle.name}</bdi></span>),
    speed: () => {
      if (vehicle.rawSpeed <= 0) return <span className="tabular-nums font-semibold" style={{ color: '#94a3b8' }}>—</span>;
      // Stale speed: mute color + show "Xm ago" so the user doesn't mistake
      // a 6h-old 157 km/h reading for a live one.
      if (vehicle.positionStale) {
        return (
          <Tooltip content={`Last fix ${vehicle.positionAgeLabel}`}>
            <span className="tabular-nums font-medium text-slate-400 dark:text-slate-500">
              {vehicle.speed}
            </span>
          </Tooltip>
        );
      }
      return (
        <span className="tabular-nums font-semibold" style={{ color: '#10b981' }}>{vehicle.speed}</span>
      );
    },
    lastUpdate: () => {
      const hrs = vehicle.lastUpdate ? dayjs().diff(dayjs(vehicle.lastUpdate), 'hour') : Infinity;
      const color = hrs >= 48 ? '#ef4444' : hrs >= 24 ? '#f59e0b' : undefined;
      return <span className="tabular-nums text-slate-400" style={color ? { color } : undefined}>{relativeTime(vehicle.lastUpdate)}</span>;
    },
    address: () => <AddressCell latitude={vehicle.latitude} longitude={vehicle.longitude} fallbackAddress={vehicle.address} />,
    driver: () => {
      const driverInfo = getDriverForPosition(position, drivers);
      return <span className="text-slate-500 truncate">{driverInfo?.name || '—'}</span>;
    },
    ignition: () => (
      <span style={{ color: vehicle.ignition ? '#10b981' : '#94a3b8' }} className="font-semibold">
        {vehicle.ignition ? t('vehicleList.on') : t('vehicleList.off')}
      </span>
    ),
    satellites: () => <span className="tabular-nums" style={{ color: vehicle.satellites < 5 ? '#ef4444' : '#64748b' }}>{vehicle.satellites}</span>,
    battery: () => vehicle.battery != null
      ? <span className="tabular-nums" style={{ color: vehicle.battery < 40 ? '#ef4444' : '#64748b' }}>{vehicle.battery}%</span>
      : <span className="text-slate-300">—</span>,
    heading: () => <span className="tabular-nums text-slate-500">{vehicle.course}°</span>,
    uniqueId: () => <span className="text-slate-500 truncate font-mono text-[10px]">{vehicle.uniqueId || '—'}</span>,
    phone: () => <span className="text-slate-500 truncate tabular-nums"><bdi>{vehicle.phone || '—'}</bdi></span>,
    model: () => <span className="text-slate-500 truncate">{vehicle.model || '—'}</span>,
    fuel: () => {
      const dev = devices?.[vehicle.id];
      const level = resolveFuelLevel({ overviewRow: fuelByDevice?.get(vehicle.id), device: dev, position });
      if (level.value == null) return <span className="text-slate-300">—</span>;
      return (
        <span className="flex items-center gap-0.5 tabular-nums">
          {Number(level.value).toFixed(1)}{level.unit}
          {level.source === 'profile' && level.percent != null && (
            <span className="text-slate-400 text-[10px] ms-1"><bdi>{Math.round(level.percent)}%</bdi></span>
          )}
          {level.calibrated && <FlaskConical size={9} className="text-blue-400 flex-shrink-0" />}
        </span>
      );
    },
  };
  return (map[colId] || (() => <span>—</span>))();
}

/** Cell renderer for people rows in compact mode. Mirrors CellValue's
 *  signature shape so the column header + cell render loops stay symmetric. */
function PersonCellValue({ colId, person, t, status, units }) {
  const lastFix = person.lastFix;
  const map = {
    status: () => (
      <Tooltip content={t(status.label)}>
        <span
          className="w-2.5 h-2.5 rounded-full inline-block"
          style={{ backgroundColor: status.color, boxShadow: `0 0 4px ${status.color}50` }}
        />
      </Tooltip>
    ),
    name: () => (
      <span className="font-semibold text-slate-700 dark:text-slate-200">
        {person.name ?? '—'}
      </span>
    ),
    role: () => (
      <span className="text-slate-500 capitalize">
        {(person.staffRole ?? 'other').replace(/_/g, ' ')}
      </span>
    ),
    lastFix: () => (
      <span className="text-slate-500 tabular-nums">
        {lastFix?.fixTime ? relativeTime(lastFix.fixTime) : '—'}
      </span>
    ),
    shift: () => {
      if (!person.shift?.startedAt) return <span className="text-slate-400">—</span>;
      const since = dayjs().diff(dayjs(person.shift.startedAt), 'minute');
      const label = since < 60 ? `${since}m` : `${Math.floor(since / 60)}h ${since % 60}m`;
      return <span className="text-slate-500 tabular-nums">{label}</span>;
    },
    phone: () => (
      <span className="text-slate-500 tabular-nums truncate"><bdi>{person.phone ?? '—'}</bdi></span>
    ),
    battery: () => {
      const b = lastFix?.batteryPct;
      if (b == null) return <span className="text-slate-400">—</span>;
      const color = b < 20 ? '#ef4444' : b < 40 ? '#f59e0b' : '#64748b';
      return <span className="tabular-nums" style={{ color }}>{b}%</span>;
    },
    speed: () => {
      const s = lastFix?.speedKmh;
      if (s == null || s <= 0) return <span className="text-slate-400">—</span>;
      return <span className="tabular-nums font-semibold text-emerald-600">{units.speedFromKmh(s).toFixed(0)}</span>;
    },
  };
  return (map[colId] || (() => <span>—</span>))();
}

/** Every tracker of a list row's vehicle — its cold points pool on one chip. */
const coldIdsOf = (v) => (v.members?.length ? v.members.map((m) => m.id) : [v.id]);

function AddressCell({ latitude, longitude, fallbackAddress }) {
  const address = useAddressLabel(latitude, longitude, fallbackAddress, 'liveMap');
  return <span className="text-slate-500 truncate">{address || '—'}</span>;
}

/** One expanded sibling-tracker sub-row (multi-tracker vehicles). Status is
 *  classified from the member's OWN position (passed in), so a pulled primary
 *  and a live sibling can show different dots inside the same car. */
function TrackerMemberRow({ member, status, position, isPrimary, silent, t }) {
  const battery = position?.attributes?.batteryLevel ?? null;
  const lastSeen = position?.fixTime
    ? relativeTime(position.fixTime)
    : (member.lastUpdate ? relativeTime(member.lastUpdate) : '—');
  return (
    <div className={`flex items-center gap-2 py-1.5 px-2 ${silent ? 'bg-red-500/10' : ''}`}>
      <StatusIndicator status={status} size={12} title={t(`status.${status}`)} />
      <span className={`flex-1 min-w-0 truncate text-[11px] ${silent ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-600 dark:text-slate-300'}`}>
        <bdi>{member.name}</bdi>
        {isPrimary && (
          <span className="ms-1.5 text-[8px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
            {t('vehicles.primary', 'Primary')}
          </span>
        )}
        {silent && (
          <span className="ms-1.5 inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400">
            <AlertTriangle size={8} /><bdi>{t('vehicles.signalLost', 'Signal lost')}</bdi>
          </span>
        )}
      </span>
      <span className="text-[10px] tabular-nums text-slate-400 flex-shrink-0">{lastSeen}</span>
      {battery != null && (
        <span className={`text-[10px] tabular-nums flex-shrink-0 ${battery < 40 ? 'text-red-500' : 'text-slate-400'}`}>
          {battery}%
        </span>
      )}
    </div>
  );
}

export default function VehicleListPanel({
  width, onDragStart, side, onStartReplay,
  statusFilter: statusFilterProp, onStatusFilterChange,
  groupFilter: groupFilterProp, onGroupFilterChange,
  companyFilter: companyFilterProp, onCompanyFilterChange,
  peopleEnabled, setPeopleEnabled,
  vehiclesEnabled, setVehiclesEnabled,
  slotBelowPeople = null,
  slotBelowPeopleHeight = null,
  onSlotHeightChange = null,
  bottomInset = 0,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);
  const selectedId = useSelector((state) => state.devices.selectedId);
  const selectedStaffUserId = useSelector((state) => state.staff?.selectedUserId);
  const staffByUserId = useSelector((state) => state.staff?.byUserId ?? {});
  const groups = useSelector((state) => state.groups.items);
  const drivers = useSelector((state) => state.drivers.items);
  // Multi-tracker vehicles: metadata (label/plate/trackers) keyed by vehicle id.
  const vehicles = useSelector((state) => state.vehicles?.items ?? {});
  const { byDevice: fuelByDevice } = useFuelLevels();
  const thresholds = useStatusThresholds();
  const vehicleStatuses = useVehicleStatuses();
  const units = useUnits();

  const ALL_COLUMNS = useMemo(() => getAllColumns(t, devices), [t, devices]);
  const ALL_PEOPLE_COLUMNS = useMemo(() => getAllPeopleColumns(t), [t]);

  const [density, setDensity] = useState(() => {
    try { const v = localStorage.getItem('trackizy_livemap_density'); return v === 'compact' || v === 'comfortable' ? v : 'comfortable'; } catch { return 'comfortable'; }
  });
  const [statusFilterLocal, setStatusFilterLocal] = useState('all');
  const statusFilter = statusFilterProp != null ? statusFilterProp : statusFilterLocal;
  const setStatusFilter = onStatusFilterChange || setStatusFilterLocal;
  const [peopleFilter, setPeopleFilter] = useState('all');
  const [peopleGroupFilter, setPeopleGroupFilter] = useState('all');
  const [staffContextMenu, setStaffContextMenu] = useState(null);
  const [groupFilterLocal, setGroupFilterLocal] = useState('all');
  const groupFilter = groupFilterProp != null ? groupFilterProp : groupFilterLocal;
  const setGroupFilter = onGroupFilterChange || setGroupFilterLocal;
  // Enterprise view: read-only, consolidated across the reseller's companies.
  const enterpriseMode = useSelector((state) => state.org?.enterprise === true);
  const [companyFilterLocal, setCompanyFilterLocal] = useState('all');
  const companyFilter = companyFilterProp != null ? companyFilterProp : companyFilterLocal;
  const setCompanyFilter = onCompanyFilterChange || setCompanyFilterLocal;
  const [search, setSearch] = useState('');
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('trackizy_livemap_colVis')); return Array.isArray(v) ? v : DEFAULT_VISIBLE; } catch { return DEFAULT_VISIBLE; }
  });
  const [colOrder, setColOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trackizy_livemap_colOrder')) || []; } catch { return []; }
  });
  const [colTogOpen, setColTogOpen] = useState(false);
  // Per-user drag-resized column widths (compact mode). A column's default
  // width stays in the catalog above; the name column defaults to 170.
  const vehicleWidths = useColumnWidths('trackizy_livemap_colWidths');
  const peopleWidths = useColumnWidths('trackizy_livemap_people_colWidths');
  const vehicleColWidth = (col) => (col.id === 'name' ? vehicleWidths.widthOf('name', 170) : col.width === 'flex' ? null : vehicleWidths.widthOf(col.id, col.width));
  // People name column mirrors the vehicles one: 170 px default, resizable.
  const peopleColWidth = (col) => (col.id === 'name' ? peopleWidths.widthOf('name', 170) : col.width === 'flex' ? null : peopleWidths.widthOf(col.id, col.width));
  const resizeHandle = (hook, colId, width) => (
    <div
      data-testid={`col-resize-${colId}`}
      role="separator"
      aria-orientation="vertical"
      className="absolute top-0 bottom-0 w-[6px] cursor-col-resize group/resize"
      style={{ insetInlineEnd: -3, zIndex: 1 }}
      onMouseDown={(e) => hook.startResize(colId, e, width)}
      onDoubleClick={(e) => { e.stopPropagation(); hook.resetWidth(colId); }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute top-1 bottom-1 w-px bg-slate-400/30 group-hover/resize:bg-blue-500/70 transition-colors" style={{ insetInlineStart: 2 }} />
    </div>
  );
  const [peopleVisibleCols, setPeopleVisibleCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('trackizy_livemap_people_colVis')); return Array.isArray(v) ? v : DEFAULT_VISIBLE_PEOPLE; } catch { return DEFAULT_VISIBLE_PEOPLE; }
  });
  const [peopleColOrder, setPeopleColOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trackizy_livemap_people_colOrder')) || []; } catch { return []; }
  });
  const [peopleColTogOpen, setPeopleColTogOpen] = useState(false);
  const [peopleDragIdx, setPeopleDragIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [sendCommandDevice, setSendCommandDevice] = useState(null);
  const [shareDevice, setShareDevice] = useState(null);
  const [drawerDevice, setDrawerDevice] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const { isPinned, togglePin } = useFavoriteVehicles();
  // Expand/collapse state for multi-tracker vehicle rows, keyed by vehicle id.
  const trackerAccordion = useAccordion();

  // Bridge: LiveMapPage dispatches these window events when the user
  // right-clicks a vehicle/staff marker on the map. The dialog state for
  // Send Command / Share / Edit lives here, so we open the existing menus
  // in-place rather than lifting all of that to the page.
  useEffect(() => {
    const onVehicleMenu = (e) => {
      const { deviceId, x, y } = e.detail || {};
      const device = devices[deviceId];
      if (!device) return;
      setStaffContextMenu(null);
      setContextMenu({ device, position: positions[deviceId], x, y });
    };
    const onStaffMenu = (e) => {
      const { userId, x, y } = e.detail || {};
      const base = staffByUserId[userId];
      if (!base) return;
      setContextMenu(null);
      setStaffContextMenu({ row: { ...base, _status: getStaffStatus(base).status }, x, y });
    };
    window.addEventListener('livemap-vehicle-context-menu', onVehicleMenu);
    window.addEventListener('livemap-staff-context-menu', onStaffMenu);
    return () => {
      window.removeEventListener('livemap-vehicle-context-menu', onVehicleMenu);
      window.removeEventListener('livemap-staff-context-menu', onStaffMenu);
    };
  }, [devices, positions, staffByUserId]);

  // Auto-refresh relative times every 30s
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  // Persist column preferences
  useEffect(() => { localStorage.setItem('trackizy_livemap_colVis', JSON.stringify(visibleCols)); }, [visibleCols]);
  useEffect(() => { if (colOrder.length) localStorage.setItem('trackizy_livemap_colOrder', JSON.stringify(colOrder)); }, [colOrder]);
  useEffect(() => { localStorage.setItem('trackizy_livemap_people_colVis', JSON.stringify(peopleVisibleCols)); }, [peopleVisibleCols]);
  useEffect(() => { if (peopleColOrder.length) localStorage.setItem('trackizy_livemap_people_colOrder', JSON.stringify(peopleColOrder)); }, [peopleColOrder]);
  useEffect(() => { localStorage.setItem('trackizy_livemap_density', density); }, [density]);

  // Virtual scroll for comfortable mode
  const scrollRef = useRef(null);
  const isAdmin = useSelector((state) => state.session.user?.administrator);
  const confirm = useConfirmDialog();

  const handleDeleteDevice = async (device) => {
    if (!await confirm({ title: t('common.confirmDelete', { name: device.name }), variant: 'destructive' })) return;
    try {
      await api.deleteDevice(device.id);
      dispatch(devicesActions.remove(device.id));
      showToast(t('settings.deviceDeleted'));
    } catch (err) {
      showToast(t('common.failedDelete') + ': ' + err.message);
    }
  };

  // Synthetic staff trackers (device_org_mapping.hidden_from_default_list =
  // true) are excluded — they live in Redux for legacy reasons (Phase 6
  // retires them) but the Live Map surfaces them through the Staff phones
  // layer + the People page instead.
  const visibleDevices = useMemo(
    () => Object.values(devices).filter((d) => !d.hidden_from_default_list),
    [devices],
  );

  // Multi-tracker grouping — collapses the 2-4 GPS trackers inside one car into
  // ONE row represented by the primary tracker. Shared pure helper so the list
  // and the map markers agree on membership + where the car "is".
  const grouping = useMemo(
    () => groupDevicesIntoVehicles(visibleDevices, positions, thresholds),
    [visibleDevices, positions, thresholds],
  );

  // The single device that stands in for each vehicle at the top level: the
  // primary when one is flagged, else the first member (defensive — a vehicle
  // with no primary would otherwise vanish entirely once its members fold out).
  const representativeIdByVehicle = useMemo(() => {
    const map = new Map();
    for (const [vid, members] of grouping.membersByVehicle) {
      map.set(vid, grouping.primaryDeviceIdByVehicle.get(vid) ?? members[0]?.id ?? null);
    }
    return map;
  }, [grouping]);

  // Enrich devices for display. A linked vehicle contributes exactly ONE row —
  // its representative tracker — enriched off the vehicle's BEST position (the
  // primary's fix while fresh, else the freshest live sibling), so a car moving
  // on a live sibling shows Moving even after the primary is pulled. Secondary
  // trackers fold out of the top level and reappear in the expander.
  const enriched = useMemo(() => {
    const { membersByVehicle, bestPositionByVehicle, suspectByVehicle, silentMemberIdsByVehicle } = grouping;
    const rows = [];
    for (const d of visibleDevices) {
      if (d.vehicleId) {
        // Only the representative surfaces; siblings are folded out.
        if (d.id !== representativeIdByVehicle.get(d.vehicleId)) continue;
        const members = membersByVehicle.get(d.vehicleId) ?? [d];
        const bestPos = bestPositionByVehicle.get(d.vehicleId) ?? null;
        // Classify from the OWNER of the best fix (not the primary device) so a
        // live sibling can keep the car healthy even when the primary is
        // offline/stale — vehicleStatuses already holds that per-device answer.
        const owner = bestPos ? members.find((m) => positions[m.id] === bestPos) : null;
        const status = owner
          ? (vehicleStatuses.get(owner.id) ?? 'offline')
          : (vehicleStatuses.get(d.id) ?? 'offline');
        const row = enrichDevice(d, bestPos, thresholds, status, units.speedUnit);
        row.name = vehicles[d.vehicleId]?.label || d.name;
        row.vehicleId = d.vehicleId;
        row.members = members;
        row.trackerCount = members.length;
        // Tamper-divergence: one tracker live while a sibling went silent.
        row.suspect = suspectByVehicle.get(d.vehicleId) === true;
        row.silentMemberIds = silentMemberIdsByVehicle.get(d.vehicleId) ?? [];
        rows.push(row);
      } else {
        // Unlinked device — untouched, first-class in the list.
        const row = enrichDevice(d, positions[d.id], thresholds, vehicleStatuses.get(d.id) ?? 'offline', units.speedUnit);
        row.trackerCount = 1;
        row.suspect = false;
        row.silentMemberIds = [];
        rows.push(row);
      }
    }
    return rows;
  }, [visibleDevices, positions, thresholds, vehicleStatuses, units.speedUnit, grouping, representativeIdByVehicle, vehicles]);

  // Cars currently flagged as suspect (tamper-divergence). Memoized to a stable
  // string key so the auto-expand effect only fires when the SET changes, not on
  // every position poll that leaves membership identical.
  const suspectVehicleIds = useMemo(
    () => enriched.filter((v) => v.suspect && v.vehicleId).map((v) => v.vehicleId),
    [enriched],
  );
  const suspectKey = useMemo(() => [...suspectVehicleIds].sort().join('|'), [suspectVehicleIds]);

  // Auto-expand a newly-suspect car ONCE to reveal its trackers, without
  // fighting the user: the ref remembers cars we've already auto-opened so a
  // manual collapse sticks; when a car recovers we forget it, so a future
  // tamper episode opens it again.
  const autoExpandedSuspectRef = useRef(new Set());
  useEffect(() => {
    const current = new Set(suspectVehicleIds);
    for (const vid of current) {
      if (!autoExpandedSuspectRef.current.has(vid)) {
        autoExpandedSuspectRef.current.add(vid);
        if (!trackerAccordion.isExpanded(vid)) trackerAccordion.toggleRow(vid);
      }
    }
    for (const vid of [...autoExpandedSuspectRef.current]) {
      if (!current.has(vid)) autoExpandedSuspectRef.current.delete(vid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suspectKey]);

  // Enterprise view: the company chip scopes the rows AND the header/pill
  // counts, so "Vehicles (n)" and the status pills agree with the top bar.
  const companyScoped = useMemo(
    () => (enterpriseMode && isCompanyFilterActive(companyFilter)
      ? enriched.filter((v) => deviceInCompany(devices[v.id], companyFilter))
      : enriched),
    [enriched, enterpriseMode, companyFilter, devices],
  );

  // Compute counts
  const counts = useMemo(() => ({
    total: companyScoped.length,
    moving: companyScoped.filter((v) => v.status === 'moving').length,
    stopped: companyScoped.filter((v) => v.status === 'stopped').length,
    idle: companyScoped.filter((v) => v.status === 'idle').length,
    pending: companyScoped.filter((v) => v.status === 'pending').length,
    offline: companyScoped.filter((v) => v.status === 'offline').length,
  }), [companyScoped]);

  // Staff rows: only present when state.staff.byUserId has been populated
  // (useStaffPositions runs in LiveMapPage; it polls and dispatches into
  // Redux when peopleEnabled is true).
  const staffRows = useMemo(() => {
    return Object.values(staffByUserId).map((row) => ({
      ...row,
      _status: getStaffStatus(row).status,
    }));
  }, [staffByUserId]);

  const peopleCounts = useMemo(() => ({
    total: staffRows.length,
    on_shift:  staffRows.filter((r) => r._status === 'on_shift').length,
    off_shift: staffRows.filter((r) => r._status === 'off_shift').length,
    overdue:   staffRows.filter((r) => r._status === 'overdue').length,
    sos:       staffRows.filter((r) => r._status === 'sos').length,
  }), [staffRows]);

  const peopleFiltered = useMemo(() => {
    let result = staffRows;
    if (peopleFilter !== 'all') result = result.filter((r) => r._status === peopleFilter);
    if (peopleGroupFilter !== 'all') result = result.filter((r) => Array.isArray(r.staffGroupIds) && r.staffGroupIds.includes(peopleGroupFilter));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.phone ?? '').toLowerCase().includes(q) ||
        (r.staffRole ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [staffRows, peopleFilter, peopleGroupFilter, search]);

  // People row hides entirely when the org has no staff at all.
  const peopleRowVisible = staffRows.length > 0;

  // Filter
  const filtered = useMemo(() => {
    let result = companyScoped;
    if (statusFilter !== 'all') result = result.filter((v) => v.status === statusFilter);
    if (groupFilter !== 'all') {
      const groupId = Number(groupFilter);
      result = result.filter((v) => devices[v.id]?.groupId === groupId);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((v) =>
        v.name.toLowerCase().includes(q) ||
        v.address.toLowerCase().includes(q) ||
        v.driver.toLowerCase().includes(q) ||
        v.uniqueId.toLowerCase().includes(q) ||
        v.phone.toLowerCase().includes(q)
      );
    }
    // Pin favourites to top
    result = [...result].sort((a, b) => {
      const ap = isPinned(a.id) ? 1 : 0;
      const bp = isPinned(b.id) ? 1 : 0;
      return bp - ap;
    });
    return result;
  }, [companyScoped, statusFilter, groupFilter, search, devices, isPinned]);

  // Ordered + filtered columns (respects drag reorder and visibility)
  const orderedAllCols = useMemo(() => {
    if (!colOrder.length) return ALL_COLUMNS;
    const map = new Map(ALL_COLUMNS.map((c) => [c.id, c]));
    const ordered = colOrder.map((id) => map.get(id)).filter(Boolean);
    // Append any new columns not in saved order
    ALL_COLUMNS.forEach((c) => { if (!colOrder.includes(c.id)) ordered.push(c); });
    return ordered;
  }, [ALL_COLUMNS, colOrder]);

  const activeCols = useMemo(() => orderedAllCols.filter((c) => visibleCols.includes(c.id)), [orderedAllCols, visibleCols]);

  const orderedAllPeopleCols = useMemo(() => {
    if (!peopleColOrder.length) return ALL_PEOPLE_COLUMNS;
    const map = new Map(ALL_PEOPLE_COLUMNS.map((c) => [c.id, c]));
    const ordered = peopleColOrder.map((id) => map.get(id)).filter(Boolean);
    ALL_PEOPLE_COLUMNS.forEach((c) => { if (!peopleColOrder.includes(c.id)) ordered.push(c); });
    return ordered;
  }, [ALL_PEOPLE_COLUMNS, peopleColOrder]);

  const activePeopleCols = useMemo(
    () => orderedAllPeopleCols.filter((c) => peopleVisibleCols.includes(c.id)),
    [orderedAllPeopleCols, peopleVisibleCols],
  );

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 72,
    overscan: 10,
  });

  const selectVehicle = (v) => {
    dispatch(staffActions.clearSelection());
    dispatch(devicesActions.selectId(v.id === selectedId ? null : v.id));
  };

  const selectStaff = (row) => {
    dispatch(devicesActions.selectId(null));
    dispatch(staffActions.selectUser(row.userId === selectedStaffUserId ? null : row.userId));
  };

  const groupList = Object.values(groups);
  const companies = useMemo(
    () => (enterpriseMode ? companyOptions(visibleDevices, groupList) : []),
    // groupList is rebuilt every render; `groups` is its stable source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enterpriseMode, visibleDevices, groups],
  );

  return (
    <div
      className="flex flex-col flex-shrink-0 z-10 relative h-full min-h-0"
      style={{
        width: width || (density === 'compact' ? 520 : 340),
        ...frost(0.72),
        borderInlineEnd: '1px solid var(--glass-border)',
        boxShadow: '4px 0 24px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header: search + density toggle on one row (asset counts live in the page top-bar) */}
      <div className="px-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.35)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <div className="relative flex-1 min-w-0">
            <Search size={13} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('vehicleList.searchPlaceholder')}
              className="w-full ps-9 pe-3 py-2 text-xs rounded-xl outline-none placeholder:text-slate-400 transition-all bg-white/50 border border-white/60 focus:bg-white/70 focus:border-blue-400/50 focus:ring-2 focus:ring-blue-500/10"
            />
          </div>
          <button
            onClick={() => setDensity('compact')}
            className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${density === 'compact' ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'}`}
          >
            <Grid3X3 size={14} />
          </button>
          <button
            onClick={() => setDensity('comfortable')}
            className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${density === 'comfortable' ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'}`}
          >
            <List size={14} />
          </button>
        </div>

        {/* Filter rows — in comfortable mode they live at the top of the
            panel; in compact mode they're moved into each section's local
            header (see below) so each section gets its own column-filter
            button alongside its filter pills. */}
        {density === 'comfortable' && (
          <>
            {/* Status filter — vehicles row */}
            <div
              className="flex items-center gap-1 px-2 py-1.5 mb-2 rounded-xl"
              style={{ background: 'rgba(16, 185, 129, 0.08)' }}
            >
              <Tooltip
                content={vehiclesEnabled
                  ? t('vehicleList.tipHideVehicles', 'Hide vehicles')
                  : t('vehicleList.tipShowVehicles', 'Show vehicles')}
              >
                <button
                  type="button"
                  onClick={() => setVehiclesEnabled?.((v) => !v)}
                  className={`p-1 rounded-md flex-shrink-0 transition-all ${vehiclesEnabled ? 'text-emerald-600 bg-emerald-500/10' : 'text-slate-400 hover:bg-white/40'}`}
                >
                  <Car size={12} />
                </button>
              </Tooltip>
              {!vehiclesEnabled && (
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  {t('nav.vehicles', 'Vehicles')}
                </span>
              )}
              {vehiclesEnabled && STATUS_FILTERS.map((f) => {
                const count = f.key === 'all' ? counts.total : counts[f.key];
                const active = statusFilter === f.key;
                const pillColor = STATUS_CONFIG[f.key]?.color || '#10b981';
                const tip = f.key === 'all'
                  ? t('vehicleList.tipAllVehicles', 'All vehicles')
                  : `${t(`status.${f.key}`)} — ${t(`status.${f.key}Desc`, STATUS_DESC_FALLBACK[f.key] || '')}`;
                return (
                  <Tooltip key={f.key} content={tip}>
                    <button
                      onClick={() => setStatusFilter(statusFilter === f.key && f.key !== 'all' ? 'all' : f.key)}
                      className={`flex items-center justify-center gap-1 flex-1 py-1.5 rounded-xl text-[10px] font-semibold transition-all ${active ? '' : 'bg-white/40 border border-white/40 text-slate-500 hover:bg-white/60'}`}
                      style={active ? activePillStyle(pillColor) : {}}
                    >
                      {f.key !== 'all' && (
                        <StatusIndicator status={f.key} size={11} style="icon" />
                      )}
                      {f.key === 'all' ? t('vehicleList.all') : count}
                    </button>
                  </Tooltip>
                );
              })}
            </div>

            {peopleRowVisible && (
              <div
                className="flex items-center gap-1 px-2 py-1.5 mb-2 rounded-xl"
                style={{ background: 'rgba(59, 130, 246, 0.08)' }}
              >
                <Tooltip
                  content={peopleEnabled
                    ? t('vehicleList.tipHidePeople', 'Hide people')
                    : t('vehicleList.tipShowPeople', 'Show people')}
                >
                  <button
                    type="button"
                    onClick={() => setPeopleEnabled?.((v) => !v)}
                    className={`p-1 rounded-md flex-shrink-0 transition-all ${peopleEnabled ? 'text-blue-600 bg-blue-500/10' : 'text-slate-400 hover:bg-white/40'}`}
                  >
                    <Users size={12} />
                  </button>
                </Tooltip>
                {!peopleEnabled && (
                  <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                    {t('vehicleList.staffSection', 'People')}
                  </span>
                )}
                {peopleEnabled && PEOPLE_STATUS_FILTERS.map((f) => {
                  const count = f.key === 'all' ? peopleCounts.total : peopleCounts[f.key];
                  const active = peopleFilter === f.key;
                  const pillColor = f.color || '#3b82f6';
                  return (
                    <Tooltip key={f.key} content={t(f.labelKey)}>
                      <button
                        onClick={() => setPeopleFilter(f.key)}
                        className={`flex items-center justify-center gap-1 flex-1 py-1.5 rounded-xl text-[10px] font-semibold transition-all ${active ? '' : 'bg-white/40 border border-white/40 text-slate-500 hover:bg-white/60'}`}
                        style={active ? activePillStyle(pillColor) : {}}
                      >
                        {f.color && (
                          <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: f.color, boxShadow: `0 0 4px ${f.color}40` }} />
                        )}
                        {f.key === 'all' ? t('vehicleList.all') : count}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
            {peopleRowVisible && peopleEnabled && (
              <div className="mb-2">
                <StaffGroupSelect compact value={peopleGroupFilter} onChange={setPeopleGroupFilter} />
              </div>
            )}
          </>
        )}

        {/* Company filter — enterprise view only (list + map markers). */}
        {enterpriseMode && companies.length > 0 && (
          <div data-testid="company-filter" className="mb-2">
            <SearchableSelect
              ariaLabel={t('enterprise.companyFilter', 'Filter by company')}
              value={isCompanyFilterActive(companyFilter) ? companyFilter : null}
              options={companies.map((c) => ({ id: c.id, label: c.name }))}
              onChange={(o) => setCompanyFilter(o ? o.id : 'all')}
              placeholder={t('enterprise.allCompanies', 'All companies')}
              clearLabel={t('enterprise.allCompanies', 'All companies')}
              searchPlaceholder={t('enterprise.searchCompanies', 'Search companies…')}
              emptyLabel={t('enterprise.noCompanyMatches', 'No companies match')}
              buttonClassName="text-[11px] rounded-xl px-3 py-1.5 text-slate-600 bg-white/50 border border-white/60"
            />
          </div>
        )}

        {/* Group filter */}
        {groupList.length > 0 && (
          <div data-testid="group-filter">
            <SearchableSelect
              ariaLabel={t('vehicleList.allGroups')}
              value={groupFilter !== 'all' && groupFilter !== '' ? groupFilter : null}
              options={[...groupList]
                .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                .map((g) => ({ id: g.id, label: g.companyName ? `${g.name} · ${g.companyName}` : g.name }))}
              onChange={(o) => setGroupFilter(o ? String(o.id) : 'all')}
              placeholder={t('vehicleList.allGroups')}
              clearLabel={t('vehicleList.allGroups')}
              searchPlaceholder={t('enterprise.searchGroups', 'Search groups…')}
              buttonClassName="text-[11px] rounded-xl px-3 py-1.5 text-slate-600 bg-white/50 border border-white/60"
            />
          </div>
        )}
      </div>

      {/* Vehicle list. Bottom padding leaves room for either the
          absolute-positioned floating TelemetryPanel overlay (bottomInset:
          its measured height, 0 when closed) OR the in-panel docked telemetry split (variable height +
          handle), so the last rows can scroll above it. */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-h-0"
        style={{
          paddingBottom: (slotBelowPeople && slotBelowPeopleHeight)
            ? slotBelowPeopleHeight + 6
            : bottomInset,
        }}
      >
        {/* Empty-state short-circuit applies ONLY to comfortable mode, where
            the status pills live in the panel header ABOVE this block. In
            compact mode the pills are inside the per-section header — short-
            circuiting here would hide the pills along with the rows, leaving
            the user stuck on a zero-count filter with nothing to click. So
            in compact mode we fall through and render the section header
            (pills + column row); the empty message lives inline below. */}
        {filtered.length === 0 && vehiclesEnabled && density !== 'compact' ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Car size={32} className="mb-2 opacity-30" />
            <p className="text-xs">{t('vehicleList.noVehicles')}</p>
          </div>
        ) : density === 'compact' ? (
          // Compact mode — per-section filter row + column-headers row stacked
          // above each section's data rows (Vehicles + People). Lets each kind
          // own its column customization independently.
          <div className="overflow-x-auto scrollbar-hide flex-1">
            {/* ── Vehicles section ─────────────────────────────────────────
                Filter rows are tinted with the cluster color (green for
                vehicles, blue for people) so the section a row belongs to
                is visible at a glance. */}
            <div className="flex items-center gap-1 px-2 py-1.5 sticky top-0 z-[2]"
              style={{ background: 'rgba(16, 185, 129, 0.08)', backdropFilter: 'blur(8px)', borderBottom: '1px solid var(--glass-border)' }}
            >
              <Tooltip
                content={vehiclesEnabled
                  ? t('vehicleList.tipHideVehicles', 'Hide vehicles')
                  : t('vehicleList.tipShowVehicles', 'Show vehicles')}
              >
                <button
                  type="button"
                  onClick={() => setVehiclesEnabled((v) => !v)}
                  className={`p-1 rounded-md flex-shrink-0 transition-all ${vehiclesEnabled ? 'text-emerald-600 bg-emerald-500/10' : 'text-slate-400 hover:bg-white/40'}`}
                >
                  <Car size={12} />
                </button>
              </Tooltip>
              {!vehiclesEnabled && (
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  {t('nav.vehicles', 'Vehicles')}
                </span>
              )}
              {vehiclesEnabled && STATUS_FILTERS.map((f) => {
                const count = f.key === 'all' ? counts.total : counts[f.key];
                const active = statusFilter === f.key;
                const pillColor = STATUS_CONFIG[f.key]?.color || '#10b981';
                const tip = f.key === 'all'
                  ? t('vehicleList.tipAllVehicles', 'All vehicles')
                  : `${t(`status.${f.key}`)} — ${t(`status.${f.key}Desc`, STATUS_DESC_FALLBACK[f.key] || '')}`;
                return (
                  <Tooltip key={f.key} content={tip}>
                    <button
                      onClick={() => setStatusFilter(statusFilter === f.key && f.key !== 'all' ? 'all' : f.key)}
                      className={`flex items-center justify-center gap-1 flex-1 py-1 rounded-lg text-[10px] font-semibold transition-all ${active ? '' : 'bg-white/40 border border-white/40 text-slate-500 hover:bg-white/60'}`}
                      style={active ? activePillStyle(pillColor) : {}}
                    >
                      {f.key !== 'all' && (
                        <StatusIndicator status={f.key} size={11} style="icon" />
                      )}
                      {f.key === 'all' ? t('vehicleList.all') : count}
                    </button>
                  </Tooltip>
                );
              })}
              {vehiclesEnabled && (
                <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Tooltip content={t('vehicleList.columns', 'Columns')}>
                    <button
                      type="button"
                      onClick={() => setColTogOpen(!colTogOpen)}
                      className={`p-1 rounded-md transition-all ${colTogOpen ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'}`}
                    >
                      <Columns3 size={14} />
                    </button>
                  </Tooltip>
                  {colTogOpen && (
                    <ColumnManager
                      columns={orderedAllCols}
                      visible={visibleCols}
                      setVisible={setVisibleCols}
                      onClose={() => setColTogOpen(false)}
                      t={t}
                      dragIdx={dragIdx}
                      setDragIdx={setDragIdx}
                      setColOrder={setColOrder}
                    />
                  )}
                </div>
              )}
            </div>
            {vehiclesEnabled && (
              <>
                {/* Row 2: vehicle column headers (NAME label dropped) */}
                <div
                  className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-wider min-w-max sticky top-[36px] z-[1]"
                  style={{ background: 'rgba(16, 185, 129, 0.06)', backdropFilter: 'blur(8px)', borderBottom: '1px solid var(--glass-border)' }}
                >
                  {/* Chevron column (multi-tracker expander) + star column */}
                  <div className="flex-shrink-0" style={{ width: 20 }} />
                  <div className="flex-shrink-0" style={{ width: 22 }} />
                  {activeCols.map((col) => {
                    const w = vehicleColWidth(col);
                    return (
                      <div
                        key={col.id}
                        className={`relative px-1 py-1.5 truncate ${w == null ? 'flex-1 min-w-[140px]' : ''} ${col.id === 'status' ? 'text-center' : ''} ${col.id === 'name' ? 'text-emerald-700 font-bold' : ''}`}
                        style={w != null ? { width: w, flexShrink: 0 } : {}}
                        title={w != null ? t('vehicleList.resizeHint', 'Drag the edge to resize, double-click to reset') : undefined}
                      >
                        {col.id === 'name'
                          ? <>{t('nav.vehicles', 'Vehicles')} <span className="text-emerald-600/70 font-normal">({counts.total})</span></>
                          : col.id === 'status' ? '' : col.label}
                        {w != null && resizeHandle(vehicleWidths, col.id, w)}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {/* Inline empty state for compact mode — kept inside the panel
                so the section header (pills + columns) above stays clickable
                and the user can toggle the active filter off. */}
            {vehiclesEnabled && filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <Car size={28} className="mb-2 opacity-30" />
                <p className="text-xs">{t('vehicleList.noVehicles', 'No vehicles match filters')}</p>
              </div>
            )}
            {/* Data rows (hidden when vehiclesEnabled is off) */}
            {vehiclesEnabled && filtered.map((v) => {
              const isMulti = v.trackerCount > 1;
              const expanded = isMulti && trackerAccordion.isExpanded(v.vehicleId);
              return (
                <Fragment key={v.id}>
                  <div
                    onClick={() => selectVehicle(v)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const pos = positions[v.id];
                      setContextMenu({ device: devices[v.id], position: pos, x: e.clientX, y: e.clientY });
                    }}
                    className={`flex items-center cursor-pointer text-[11px] transition-all min-w-max ${selectedId === v.id ? 'bg-blue-500/12' : 'hover:bg-white/30'}`}
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    {/* Chevron expander (multi-tracker only; spacer otherwise so
                        columns stay aligned with the header). */}
                    <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 20 }}>
                      {isMulti && (
                        <Tooltip content={t('vehicles.showTrackers', 'Show trackers')}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); trackerAccordion.toggleRow(v.vehicleId); }}
                            className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-white/50 transition-all"
                            aria-expanded={expanded}
                          >
                            <ChevronRight size={13} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                    <Tooltip content={isPinned(v.id) ? t('vehicles.unpin') : t('vehicles.pin')}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); togglePin(v.id); }}
                        className="flex-shrink-0 p-0.5 mx-0.5"
                      >
                        <Star size={14} className={isPinned(v.id) ? 'fill-amber-400 text-amber-400' : 'text-slate-400 dark:text-slate-500'} />
                      </button>
                    </Tooltip>
                    {activeCols.map((col) => (
                      <div
                        key={col.id}
                        className={`px-1 py-[5px] truncate ${vehicleColWidth(col) == null ? 'flex-1 min-w-[140px]' : ''} ${col.id === 'status' ? 'relative flex items-center justify-center' : ''}`}
                        style={vehicleColWidth(col) != null ? { width: vehicleColWidth(col), flexShrink: 0 } : {}}
                      >
                        {col.id === 'name' && isMulti ? (
                          // Name truncates (flex-1 min-w-0) so a long label + the
                          // compact icon badges never grow this cell and shift the
                          // SPEED/UPDATE/... columns out of alignment. Suspect is an
                          // icon-only chip here (full text lives in its tooltip).
                          <span className="flex items-center gap-1 min-w-0 w-full">
                            {v.companyName ? (
                              <span className="flex flex-col min-w-0 flex-1 leading-tight">
                                <span className="truncate font-semibold text-slate-800 dark:text-slate-200"><bdi>{v.name}</bdi></span>
                                <CompanyLine name={v.companyName} />
                              </span>
                            ) : (
                              <span className="truncate font-semibold text-slate-800 dark:text-slate-200 min-w-0 flex-1"><bdi>{v.name}</bdi></span>
                            )}
                            <ColdTempChip size="sm" deviceIds={coldIdsOf(v)} />
                            <span className="flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-bold bg-emerald-500/12 text-emerald-700 dark:text-emerald-300 flex-shrink-0" title={t('vehicles.trackerCount', '{{count}} trackers', { count: v.trackerCount })}>
                              <Layers size={9} />{v.trackerCount}
                            </span>
                            {v.suspect && (
                              <span
                                className="flex items-center justify-center rounded bg-red-500/15 text-red-600 dark:text-red-400 flex-shrink-0"
                                style={{ width: 15, height: 15 }}
                                title={t('vehicles.suspectTamper', 'Tracker removed?')}
                                aria-label={t('vehicles.suspectTamper', 'Tracker removed?')}
                              >
                                <AlertTriangle size={9} />
                              </span>
                            )}
                          </span>
                        ) : (
                          col.id === 'name' ? (
                            <ColdChipWrap inline size="sm" deviceIds={coldIdsOf(v)}>
                              <CellValue colId={col.id} col={col} vehicle={v} t={t} position={positions[v.id]} drivers={drivers} devices={devices} fuelByDevice={fuelByDevice} />
                            </ColdChipWrap>
                          ) : (
                            <CellValue colId={col.id} col={col} vehicle={v} t={t} position={positions[v.id]} drivers={drivers} devices={devices} fuelByDevice={fuelByDevice} />
                          )
                        )}
                      </div>
                    ))}
                  </div>
                  {expanded && (
                    <div className="min-w-max bg-white/25 dark:bg-slate-800/20" style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      {v.members.map((m) => (
                        <div key={m.id} className="ps-8 pe-3">
                          <TrackerMemberRow
                            member={m}
                            status={vehicleStatuses.get(m.id) ?? 'offline'}
                            position={positions[m.id]}
                            isPrimary={m.isPrimary === true}
                            silent={(v.silentMemberIds ?? []).includes(m.id)}
                            t={t}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        ) : vehiclesEnabled ? (
          // Comfortable mode — virtualized card rows (skipped when the vehicles
          // category toggle is off; people section still renders below).
          <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const v = filtered[vItem.index];
              const sc = STATUS_CONFIG[v.status];
              const isMulti = v.trackerCount > 1;
              const expanded = isMulti && trackerAccordion.isExpanded(v.vehicleId);
              return (
                <div
                  key={v.id}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  className={`transition-all ${selectedId === v.id ? 'bg-blue-500/12 border-s-2 border-s-blue-500' : 'hover:bg-white/30 border-s-2 border-s-transparent'}`}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${vItem.start}px)`, borderBottom: '1px solid var(--glass-border)' }}
                >
                {/* Row body — clicking selects the primary device / flies the map. */}
                <div
                  onClick={() => selectVehicle(v)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    const pos = positions[v.id];
                    setContextMenu({ device: devices[v.id], position: pos, x: e.clientX, y: e.clientY });
                  }}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                >
                {/* Pin star */}
                <Tooltip content={isPinned(v.id) ? t('vehicles.unpin') : t('vehicles.pin')}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); togglePin(v.id); }}
                    className="flex-shrink-0 p-0.5"
                  >
                    <Star size={16} className={isPinned(v.id) ? 'fill-amber-400 text-amber-400' : 'text-slate-400 dark:text-slate-500'} />
                  </button>
                </Tooltip>

                {/* Status icon */}
                <span className="relative flex-shrink-0">
                  <span className="w-9 h-9 rounded-xl bg-white/50 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                    <DeviceMarkerIcon device={v} size={18} color={sc.color} />
                  </span>
                  {/* Status badge in the bottom-right corner — uses StatusIndicator
                      so the user's display-style preference flows through here too. */}
                  <span className="absolute -bottom-0.5 -end-0.5 rounded-full border-2 border-white/80 bg-white/95 p-0.5 flex items-center justify-center">
                    <StatusIndicator
                      status={v.status}
                      size={10}
                      style="icon"
                      title={`${t(`status.${v.status}`)} — ${t(`status.${v.status}Desc`, STATUS_DESC_FALLBACK[v.status] || '')}`}
                    />
                  </span>
                </span>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-semibold truncate text-slate-800 dark:text-slate-200"><bdi>{v.name}</bdi></p>
                      {isMulti && (
                        <>
                          {/* ⛁ N trackers badge + chevron expander */}
                          <span
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-emerald-500/12 text-emerald-700 dark:text-emerald-300 flex-shrink-0"
                            title={t('vehicles.trackerCount', '{{count}} trackers', { count: v.trackerCount })}
                          >
                            <Layers size={9} />{v.trackerCount}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); trackerAccordion.toggleRow(v.vehicleId); }}
                            className="flex-shrink-0 p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-white/50 transition-all"
                            aria-expanded={expanded}
                            title={t('vehicles.showTrackers', 'Show trackers')}
                          >
                            <ChevronRight size={13} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
                          </button>
                        </>
                      )}
                      {/* Tamper-divergence warning — a sibling went silent while
                          another stayed live (likely a pulled tracker). */}
                      {v.suspect && (
                        <span
                          className="flex items-center justify-center rounded-md bg-red-500/15 text-red-600 dark:text-red-400 flex-shrink-0"
                          style={{ width: 18, height: 18 }}
                          title={t('vehicles.suspectTamper', 'Tracker removed?')}
                          aria-label={t('vehicles.suspectTamper', 'Tracker removed?')}
                        >
                          <AlertTriangle size={11} />
                        </span>
                      )}
                    </div>
                    <ColdChipWrap deviceIds={coldIdsOf(v)}>
                      <span className="text-xs tabular-nums ms-2 flex-shrink-0" style={{ color: v.lastUpdate && dayjs().diff(dayjs(v.lastUpdate), 'hour') >= 48 ? '#ef4444' : v.lastUpdate && dayjs().diff(dayjs(v.lastUpdate), 'hour') >= 24 ? '#f59e0b' : '#94a3b8' }}>{relativeTime(v.lastUpdate)}</span>
                    </ColdChipWrap>
                  </div>
                  {v.companyName && <CompanyLine name={v.companyName} className="text-[10px]" />}
                  <div className="flex items-center gap-2 mt-0.5">
                    {v.rawSpeed > 0 && (
                      <Tooltip content={v.positionStale ? `Last fix ${v.positionAgeLabel}` : null}>
                        <span
                          className={`text-xs ${v.positionStale ? 'font-medium text-slate-400 dark:text-slate-500' : 'font-semibold text-emerald-600'}`}
                        >
                          <bdi>{v.speed} {units.speedLabel}{v.positionStale ? ` (${v.positionAgeLabel})` : ''}</bdi>
                        </span>
                      </Tooltip>
                    )}
                    <p className="text-xs truncate"><AddressCell latitude={v.latitude} longitude={v.longitude} fallbackAddress={v.address} /></p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Power size={10} className={v.ignition ? 'text-emerald-500' : 'text-slate-300'} />
                    <Locate size={10} className={v.satellites > 5 ? 'text-blue-500' : 'text-slate-300'} />
                    {v.battery != null && (
                      <span className={`text-[9px] tabular-nums font-medium ${v.battery < 40 ? 'text-red-500' : 'text-slate-400'}`}>
                        {v.battery}%
                      </span>
                    )}
                    {v.rawSpeed > 90 && !v.positionStale && <AlertTriangle size={10} className="text-red-500" />}
                    {onStartReplay && (
                      <Tooltip content={t('miniReplay.replayRoute')}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onStartReplay(v.id, v.name, e.currentTarget);
                          }}
                          className="p-1 rounded-lg text-slate-300 hover:bg-white/40 hover:text-blue-500 transition-all ms-auto"
                        >
                          <History size={11} />
                        </button>
                      </Tooltip>
                    )}
                    {!enterpriseMode && (<>
                    <Tooltip content={t('sharing.share')}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShareDevice(devices[v.id]); }}
                        className={`p-1 rounded-lg text-slate-300 hover:bg-white/40 hover:text-blue-500 transition-all ${!onStartReplay ? 'ms-auto' : ''}`}
                      >
                        <Share2 size={11} />
                      </button>
                    </Tooltip>
                    <Tooltip content={t('settings.sendCommand')}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSendCommandDevice(devices[v.id]); }}
                        className="p-1 rounded-lg text-slate-300 hover:bg-white/40 hover:text-blue-500 transition-all"
                      >
                        <Terminal size={11} />
                      </button>
                    </Tooltip>
                    </>)}
                    {isAdmin && !enterpriseMode && (
                      <>
                        <Tooltip content={t('settings.editDevice')}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDrawerDevice(devices[v.id]); }}
                            className="p-1 rounded-lg text-slate-300 hover:bg-white/40 hover:text-slate-600 transition-all"
                          >
                            <Edit3 size={11} />
                          </button>
                        </Tooltip>
                        <Tooltip content={t('common.delete')}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteDevice(devices[v.id]); }}
                            className="p-1 rounded-lg text-slate-300 hover:bg-white/40 hover:text-red-500 transition-all"
                          >
                            <Trash2 size={11} />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
                </div>
                {/* Expanded sibling trackers — rendered INSIDE the measured row
                    so the virtualizer reflows the row's height on toggle. */}
                {expanded && (
                  <div className="px-4 pb-2">
                    <div className="rounded-lg bg-white/40 dark:bg-slate-800/30 border border-white/50 overflow-hidden divide-y divide-slate-200/40 dark:divide-slate-700/40">
                      {v.members.map((m) => (
                        <TrackerMemberRow
                          key={m.id}
                          member={m}
                          status={vehicleStatuses.get(m.id) ?? 'offline'}
                          position={positions[m.id]}
                          isPrimary={m.isPrimary === true}
                          silent={(v.silentMemberIds ?? []).includes(m.id)}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : null}

        {/* ── People section ──────────────────────────────────────────────
            In compact mode the filter row is ALWAYS rendered when the org
            has any staff (peopleRowVisible) — that keeps the person-icon
            toggle reachable to re-enable the section after it's been hidden.
            Only the column headers and data rows are gated on peopleEnabled. */}
        {peopleRowVisible && density === 'compact' && (
          <>
            <div className="flex items-center gap-1 px-2 py-1.5 mt-3"
              style={{ background: 'rgba(59, 130, 246, 0.08)', backdropFilter: 'blur(8px)', borderTop: '1px solid var(--glass-border)', borderBottom: '1px solid var(--glass-border)' }}
            >
              <Tooltip
                content={peopleEnabled
                  ? t('vehicleList.tipHidePeople', 'Hide people')
                  : t('vehicleList.tipShowPeople', 'Show people')}
              >
                <button
                  type="button"
                  onClick={() => setPeopleEnabled?.((v) => !v)}
                  className={`p-1 rounded-md flex-shrink-0 transition-all ${peopleEnabled ? 'text-blue-600 bg-blue-500/10' : 'text-slate-400 hover:bg-white/40'}`}
                >
                  <Users size={12} />
                </button>
              </Tooltip>
              {!peopleEnabled && (
                <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                  {t('vehicleList.staffSection', 'People')}
                </span>
              )}
              {peopleEnabled && PEOPLE_STATUS_FILTERS.map((f) => {
                const count = f.key === 'all' ? peopleCounts.total : peopleCounts[f.key];
                const active = peopleFilter === f.key;
                const pillColor = f.color || '#3b82f6';
                return (
                  <Tooltip key={f.key} content={t(f.labelKey)}>
                    <button
                      onClick={() => setPeopleFilter(f.key)}
                      className={`flex items-center justify-center gap-1 flex-1 py-1 rounded-lg text-[10px] font-semibold transition-all ${active ? '' : 'bg-white/40 border border-white/40 text-slate-500 hover:bg-white/60'}`}
                      style={active ? activePillStyle(pillColor) : {}}
                    >
                      {f.color && (
                        <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: f.color, boxShadow: `0 0 4px ${f.color}40` }} />
                      )}
                      {f.key === 'all' ? t('vehicleList.all') : count}
                    </button>
                  </Tooltip>
                );
              })}
              {peopleEnabled && (
                <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Tooltip content={t('vehicleList.columns', 'Columns')}>
                    <button
                      type="button"
                      onClick={() => setPeopleColTogOpen(!peopleColTogOpen)}
                      className={`p-1 rounded-md transition-all ${peopleColTogOpen ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'}`}
                    >
                      <Columns3 size={14} />
                    </button>
                  </Tooltip>
                  {peopleColTogOpen && (
                    <ColumnManager
                      columns={orderedAllPeopleCols}
                      visible={peopleVisibleCols}
                      setVisible={setPeopleVisibleCols}
                      onClose={() => setPeopleColTogOpen(false)}
                      t={t}
                      dragIdx={peopleDragIdx}
                      setDragIdx={setPeopleDragIdx}
                      setColOrder={setPeopleColOrder}
                    />
                  )}
                </div>
              )}
            </div>
            {peopleEnabled && (
              <div className="px-2 py-1.5">
                <StaffGroupSelect compact value={peopleGroupFilter} onChange={setPeopleGroupFilter} />
              </div>
            )}
            {peopleEnabled && (
              <div
                className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-wider min-w-max"
                style={{ background: 'rgba(59, 130, 246, 0.06)', backdropFilter: 'blur(8px)', borderBottom: '1px solid var(--glass-border)' }}
              >
                <div className="flex-shrink-0" style={{ width: 22 }} />
                {activePeopleCols.map((col) => {
                  const w = peopleColWidth(col);
                  return (
                    <div
                      key={col.id}
                      className={`relative px-1 py-1.5 truncate ${w == null ? 'flex-1 min-w-[140px]' : ''} ${col.id === 'status' ? 'text-center' : ''} ${col.id === 'name' ? 'text-blue-700 font-bold' : ''}`}
                      style={w != null ? { width: w, flexShrink: 0 } : {}}
                      title={w != null ? t('vehicleList.resizeHint', 'Drag the edge to resize, double-click to reset') : undefined}
                    >
                      {col.id === 'name'
                        ? <>{t('vehicleList.staffSection', 'People')} <span className="text-blue-600/70 font-normal">({peopleCounts.total})</span></>
                        : col.id === 'status' ? '' : col.label}
                      {w != null && resizeHandle(peopleWidths, col.id, w)}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        {peopleEnabled && peopleRowVisible && peopleFiltered.length > 0 && (
          <div>
            {density === 'comfortable' && (
              <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 bg-white/30">
                <Users size={11} />
                {t('vehicleList.staffSection', 'People')}
                <span className="text-slate-400 font-normal">({peopleFiltered.length})</span>
              </div>
            )}
            {peopleFiltered.map((row) => {
              const status = getStaffStatus(row);
              const lastFix = row.lastFix;
              const isSelected = selectedStaffUserId === row.userId;
              const fixAge = lastFix?.fixTime ? relativeTime(lastFix.fixTime) : '—';
              // Same translated source as the People roster and StaffDrawer —
              // an underscore-stripped raw value rendered English in ar/ckb.
              const staffRoleText = roleLabel(row.staffRole, t);

              if (density === 'compact') {
                return (
                  <div
                    key={row.userId}
                    onClick={() => selectStaff(row)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setStaffContextMenu({ row, x: e.clientX, y: e.clientY });
                    }}
                    className={`flex items-center cursor-pointer text-[11px] transition-all min-w-max ${isSelected ? 'bg-blue-500/12' : 'hover:bg-white/30'}`}
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    {/* Star slot — placeholder for future favorite-people feature */}
                    <Tooltip content={t('vehicles.pin')}>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 p-0.5 mx-0.5 opacity-40 cursor-default"
                        tabIndex={-1}
                      >
                        <Star size={14} className="text-slate-400 dark:text-slate-500" />
                      </button>
                    </Tooltip>
                    {activePeopleCols.map((col) => (
                      <div
                        key={col.id}
                        className={`px-1 py-[5px] truncate ${peopleColWidth(col) == null ? 'flex-1 min-w-[140px]' : ''} ${col.id === 'status' ? 'relative flex items-center justify-center' : ''}`}
                        style={peopleColWidth(col) != null ? { width: peopleColWidth(col), flexShrink: 0 } : {}}
                      >
                        <PersonCellValue colId={col.id} person={row} t={t} status={status} units={units} />
                      </div>
                    ))}
                  </div>
                );
              }

              // Comfortable: card row matching the vehicle comfortable card structure
              return (
                <div
                  key={row.userId}
                  onClick={() => selectStaff(row)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setStaffContextMenu({ row, x: e.clientX, y: e.clientY });
                  }}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-blue-500/12 border-s-2 border-s-blue-500'
                      : 'hover:bg-white/30 border-s-2 border-s-transparent'
                  }`}
                  style={{ borderBottom: '1px solid var(--glass-border)' }}
                >
                  {/* Star slot — placeholder for future favorite-people feature */}
                  <Tooltip content={t('vehicles.pin')}>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="flex-shrink-0 p-0.5 opacity-40 cursor-default"
                      tabIndex={-1}
                    >
                      <Star size={16} className="text-slate-400 dark:text-slate-500" />
                    </button>
                  </Tooltip>
                  {/* Status icon — mirrors vehicle marker tile + status dot */}
                  <span className="relative flex-shrink-0">
                    <span className="w-9 h-9 rounded-xl bg-white/50 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                      <User size={18} style={{ color: status.color }} />
                    </span>
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white/80"
                      style={{ backgroundColor: status.color, boxShadow: `0 0 6px ${status.color}50` }}
                    />
                  </span>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold truncate text-slate-800 dark:text-slate-200">
                        {row.name ?? '—'}
                      </p>
                      <span className="text-xs tabular-nums ms-2 text-slate-400">{fixAge}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] font-semibold capitalize" style={{ color: status.color }}>
                        {t(status.label)}
                      </span>
                      <span className="text-[11px] text-slate-500 truncate"><bdi>{staffRoleText}</bdi></span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Inline slot fallback — only used when split mode is not
            requested (slotBelowPeopleHeight === null). Today's docked
            mode always passes a height, so this branch only runs in
            ad-hoc callers. */}
        {slotBelowPeople && !slotBelowPeopleHeight && (
          <div className="flex-shrink-0 mt-3">
            {slotBelowPeople}
          </div>
        )}
      </div>

      {/* Split-mode slot: absolute-positioned at the panel bottom
          with a drag handle. The scroll container above gets a
          matching paddingBottom so the last rows can scroll above
          the slot instead of being hidden. */}
      {slotBelowPeople && slotBelowPeopleHeight && (
        <div
          className="absolute left-0 right-0 bottom-0 flex flex-col"
          style={{
            height: slotBelowPeopleHeight + 6,
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          }}
        >
          {onSlotHeightChange && (
            <DockResizeHandle
              value={slotBelowPeopleHeight}
              onChange={onSlotHeightChange}
              direction="up-grows"
            />
          )}
          <div className="flex-1 overflow-y-auto min-h-0">
            {slotBelowPeople}
          </div>
        </div>
      )}

      {/* Footer removed — total summary moved into the panel header. */}

      {sendCommandDevice && (
        <SendCommandDialog device={sendCommandDevice} onClose={() => setSendCommandDevice(null)} />
      )}

      {shareDevice && (
        <ShareDialog
          deviceId={shareDevice.id}
          deviceName={shareDevice.name}
          open={!!shareDevice}
          onClose={() => setShareDevice(null)}
        />
      )}

      <DeviceDrawer
        device={drawerDevice}
        open={drawerDevice !== null}
        onClose={() => setDrawerDevice(null)}
      />

      {contextMenu && (
        <VehicleContextMenu
          device={contextMenu.device}
          position={contextMenu.position}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          // Enterprise view is read-only: no command, share, replay or edit.
          onSendCommand={enterpriseMode ? undefined : (d) => { setContextMenu(null); setSendCommandDevice(d); }}
          onShareLink={enterpriseMode ? undefined : (d) => { setContextMenu(null); setShareDevice(d); }}
          onStartReplay={enterpriseMode || !onStartReplay ? undefined : (d) => { setContextMenu(null); onStartReplay(d.id, d.name); }}
          onEditDevice={enterpriseMode ? undefined : (d) => { setContextMenu(null); setDrawerDevice(d); }}
        />
      )}

      {staffContextMenu && (
        <StaffContextMenu
          row={staffContextMenu.row}
          x={staffContextMenu.x}
          y={staffContextMenu.y}
          onClose={() => setStaffContextMenu(null)}
        />
      )}

      {/* Resize handle */}
      {onDragStart && (
        <div
          className="absolute top-0 bottom-0 flex items-center justify-center"
          style={{
            width: 6,
            cursor: 'ew-resize',
            [side === 'start' ? 'insetInlineEnd' : 'insetInlineStart']: -3,
          }}
          onMouseDown={onDragStart}
        >
          <div className="w-[3px] h-10 rounded-full bg-slate-400/30 hover:bg-blue-400/50 transition-colors" />
        </div>
      )}
    </div>
  );
}
